import { formatMoney, formatMoneyExact, type Cents } from './money.ts';

/**
 * The money bar: what is true about a job's money without a price. The
 * estimate was stripped out until it is built again on the steps (Jason,
 * 2026-10-07: "this whole estimate feature needs to be stripped out till we
 * re-implement it correctly"), so nothing priced, allowed for or agreed is
 * drawn; showing no number is more honest than a confident one. The same for
 * NSB and the client:
 *
 *   billed      what has been invoiced for the work           (upper sub-bar)
 *   money in    what has been paid: payments, deposits and     (lower sub-bar)
 *               retainers, less refunds. Short of billed, the
 *               gap is billed and not yet paid (red); past it,
 *               money paid ahead, a deposit not yet used up
 *
 * The client's budget, confirmed and one waiting, are lines on it (the DTO's
 * `ceiling` and `pendingBudget`). A job that ended early owes nothing more,
 * and what came in past what was billed goes back.
 */

export interface CostBarInput {
  /** What has been billed, at client price, none voided. */
  billedCents: Cents;
  /** What has come in: payments, deposits and retainers, less refunds; none voided. */
  moneyInCents: Cents;
  /** The job ended early: nothing more is owed. */
  ended?: boolean;
}

export interface CostBar {
  billedCents: Cents;
  moneyInCents: Cents;
  /** Billed and not yet paid. */
  unpaidCents: Cents;
  /** Money in past billed: paid ahead (a deposit not yet used up), or, on a job that ended, to give back. */
  aheadCents: Cents;
  /** How far the bar reaches: billed or money in, whichever is further. */
  totalCents: Cents;
  ended: boolean;
}

/** The bar, or null while nothing is billed and nothing has come in: blank, never a bar of noughts. */
export function computeCostBar(input: CostBarInput): CostBar | null {
  const { billedCents, moneyInCents } = input;
  if (billedCents <= 0 && moneyInCents <= 0) return null;
  return {
    billedCents,
    moneyInCents,
    unpaidCents: Math.max(0, billedCents - moneyInCents),
    aheadCents: Math.max(0, moneyInCents - billedCents),
    totalCents: Math.max(billedCents, moneyInCents),
    ended: input.ended ?? false,
  };
}

/** Whole dollars, as anything client-facing is, except a sum under a dollar: a cent owed is never "$0". */
const money = (cents: Cents) => (cents !== 0 && Math.abs(cents) < 50 ? formatMoneyExact(cents) : formatMoney(cents));

/** The one sentence above the bar. */
export function costBarSentence(bar: CostBar | null): string {
  if (bar === null) return 'Nothing has been billed or paid yet.';
  if (bar.ended) {
    const back = bar.aheadCents > 0 ? `, and ${money(bar.aheadCents)} of what came in goes back to the client` : '';
    return `This project ended. ${money(bar.billedCents)} was billed for the work that was done${back}; nothing else is owed.`;
  }
  if (bar.billedCents <= 0) return `${money(bar.moneyInCents)} has come in, ahead of the first bill.`;
  if (bar.unpaidCents > 0) return `${money(bar.billedCents)} is billed, ${money(bar.unpaidCents)} of it not yet paid.`;
  if (bar.aheadCents > 0) return `${money(bar.billedCents)} is billed and paid, and ${money(bar.aheadCents)} more has come in ahead of the next bills.`;
  return `${money(bar.billedCents)} is billed and paid.`;
}
